import { Component, inject, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { take } from "rxjs/operators";
import { ChatbotManagementService } from "@app/services/chatbot-management.service";
import { ViewConfig } from "@app/commons/utils/view-config";
import {
  IBot,
  IBotInactiveDoc,
} from "@app/commons/types/interfaces/bots.interface";
import { getErrorMessage } from "@app/commons/utils/utils";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { DialogComponent } from "@app/components/dialog/dialog.component";
import { SnackbarService } from "@app/services/snackbar.service";
import {
  AllPageSizes,
  PageConfigDefault,
} from "@app/commons/types/enums/constants.enums";

@Component({
  selector: "app-chatbot-detail",
  templateUrl: "./chatbot-detail.component.html",
  styleUrl: "./chatbot-detail.component.scss",
})
export class ChatbotDetailComponent {
  modalService = inject(NgbModal);
  viewConfig = new ViewConfig();
  page = PageConfigDefault.PageLimit;
  pageSize = PageConfigDefault.PageSize;
  pageInactive = PageConfigDefault.PageLimit;
  pageInactiveSize = PageConfigDefault.PageSize;
  allPageSizes = Object.keys(AllPageSizes).filter((key) => !isNaN(Number(key)));
  collectionSize = 0;
  collectionInactiveSize = 0;
  botDetails: any;
  botDocuments: any[] = [];
  manageDocuments: any = [];
  @ViewChild("manageDocumentsModal") manageDocumentsTempRef: any;
  id: string | null = null;

  fileToUpload: File | null = null;
  selectedFile: File | null = null;
  enabledCheckbox: boolean = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private chatbotManagementService: ChatbotManagementService,
    private snackBarService: SnackbarService,
    private router: Router
  ) {
    this.activatedRoute.params.subscribe({
      next: (data: any) => {
        if (data["id"]) {
          this.getDetails(data["id"]);
        }
      },
    });
  }

  triggerFileInput(): void {
    document.getElementById("fileInput")?.click();
  }

  handleFileInput(files: any): void {
    if (files?.files?.length) {
      this.fileToUpload = files.files[0];
      this.uploadFile();
    }
  }

  uploadFile(): void {
    if (!this.fileToUpload) {
      alert("Please select a file to upload");
      return;
    }
    this.viewConfig.setLoading(true);
    const formData: FormData = new FormData();
    formData.append("file", this.fileToUpload, this.fileToUpload.name);
    formData.append("bucketName", this.botDetails.bucketPath);
    this.chatbotManagementService
      .addDocumentToBot(this.botDetails.id, formData)
      .subscribe({
        next: (data) => {
          this.snackBarService.show("Document has been added.");
          this.selectedFile = null;
          this.viewConfig.setLoading(false);
          this.refreshInactiveData();
        },
        error: (error) => {
          console.log(error);
          this.selectedFile = null;
          this.snackBarService.show(getErrorMessage(error));
          this.viewConfig.setLoading(false);
        },
      });
  }

  getDetails(id: string) {
    this.id = id;
    this.viewConfig.setLoading(true);
    this.chatbotManagementService
      .getByName(id, {
        page: this.page,
        pageSize: this.pageSize,
      })
      .subscribe({
        next: (result: IBot) => {
          this.viewConfig.setLoading(false);
          this.botDetails = result;
          this.enabledCheckbox = this.botDetails.enabled;
          this.botDocuments = result.documents;
          this.collectionSize = result.total;
          this.collectionInactiveSize = result.totalInactive;
          this.manageDocuments = result.allDocuments;
        },
        error: (e) => {
          this.viewConfig.setError(getErrorMessage(e));
        },
      });
  }

  manage() {
    const modalRef = this.modalService.open(this.manageDocumentsTempRef, {
      centered: true,
      size: "xl",
    });
    this.pageInactive = PageConfigDefault.PageLimit;
    this.pageInactiveSize = PageConfigDefault.PageSize;
    this.refreshInactiveData();
    modalRef.shown.pipe(take(1)).subscribe(() => {
      document.getElementById("chatbot-manage-docs-table")?.focus();
    });
  }

  private focusDocumentsTableAfterDialog(): void {
    (
      document.getElementById("chatbot-manage-docs-table") ??
      document.getElementById("chatbot-detail-documents-table")
    )?.focus();
  }

  refreshData() {
    this.getDetails(this.id ?? "");
  }

  markActivateOrInactivate(document: any) {
    const payload = {
      action: !document.isActive,
    };
    console.log(payload);

    const modalRef = this.modalService.open(DialogComponent, {
      backdrop: true,
    });
    modalRef.componentInstance.alert = {
      message: "",
      type: "",
    };
    modalRef.componentInstance.title = `Are you sure?`;
    modalRef.componentInstance.message = `Are you sure you want to ${
      !payload.action ? "remove" : "add"
    } this document ${!payload.action ? "from" : "to"} the knowledge base?`;
    modalRef.componentInstance.buttons = [
      {
        id: 0,
        type: "outline-dark",
        class: "ind-button-md",
        label: "Cancel",
        onClick: () => {
          modalRef.close();
        },
      },
      {
        id: 1,
        type: "primary",
        class: "ind-button-md",
        label: !payload.action ? "Remove" : "Add",
        onClick: () => {
          this.viewConfig.setLoading(true);
          this.chatbotManagementService
            .activateAction(document.id, payload)
            .subscribe({
              next: (result: any) => {
                this.getDetails(this.botDetails.id);
                modalRef.componentInstance.alert = {
                  message: `Document has been ${
                    !payload.action ? "removed" : "added"
                  } from knowledge base.`,
                  type: "success",
                };
                modalRef.componentInstance.message = "";
                modalRef.componentInstance.buttons = [];
                this.viewConfig.setLoading(false);
                setTimeout(() => {
                  modalRef.close();
                  queueMicrotask(() =>
                    this.focusDocumentsTableAfterDialog(),
                  );
                }, 2000);
              },
              error: (e) => {
                modalRef.componentInstance.alert = {
                  message: getErrorMessage(e),
                  type: "danger",
                };
                this.viewConfig.setLoading(false);
                // this.viewConfig.setError(getErrorMessage(e));
              },
            });
        },
      },
    ];
  }

  previewFile(document: any) {
    this.chatbotManagementService
      .getPresignedURL(document.fileFullName, document.bucketName)
      .subscribe((data: any) => {
        window.open(data.url);
      });
  }

  refreshInactiveData() {
    this.viewConfig.setLoading(true);
    this.chatbotManagementService
      .getByNameInactiveDocuments(this.id ?? "", {
        page: this.pageInactive,
        pageSize: this.pageInactiveSize,
      })
      .subscribe({
        next: (result: IBotInactiveDoc) => {
          this.viewConfig.setLoading(false);
          this.manageDocuments = result.data;
          this.collectionInactiveSize = result.total;
        },
        error: (e) => {
          this.viewConfig.setError(getErrorMessage(e));
        },
      });
  }

  deleteDocument(id: string) {
    this.viewConfig.setLoading(true);
    this.chatbotManagementService.deleteDocument(id).subscribe({
      next: (result: any) => {
        this.viewConfig.setLoading(false);
        this.refreshInactiveData();
      },
      error: (e) => {
        this.viewConfig.setError(getErrorMessage(e));
      },
    });
  }

  goBack() {
    this.router.navigate(["admin", "chatbot-management"]);
  }

  /**
   * Enter does not toggle checkboxes by default; Space can be unreliable on
   * visually hidden inputs. Use the same path as a click so ngModelChange runs.
   */
  onChatbotActivateCheckboxKeydown(event: Event): void {
    const e = event as KeyboardEvent;
    if (e.repeat) {
      return;
    }
    const isToggle =
      e.key === "Enter" ||
      e.key === " " ||
      e.key === "Spacebar" ||
      e.code === "Space";
    if (!isToggle) {
      return;
    }
    e.preventDefault();
    (e.currentTarget as HTMLInputElement).click();
  }

  changeEnableStatus(): void {
    const value = this.enabledCheckbox;
    const modalRef = this.modalService.open(DialogComponent, {
      backdrop: true,
    });
    modalRef.componentInstance.alert = {
      message: "",
      type: "",
    };
    modalRef.componentInstance.title = `Change Chatbot Status`;
    modalRef.componentInstance.message = `Are you sure you want to ${
      value ? "activate" : "inactivate"
    } the chatbot`;
    modalRef.componentInstance.buttons = [
      {
        id: 0,
        type: "outline-dark",
        class: "ind-button-md",
        label: "No",
        onClick: () => {
          this.enabledCheckbox = !value;
          modalRef.close();
        },
      },
      {
        id: 1,
        type: "primary",
        class: "ind-button-md",
        label: "Yes",
        onClick: () => {
          this.viewConfig.setLoading(true);
          this.chatbotManagementService
            .enabledChatbotChange(this.id, value)
            .subscribe({
              next: () => {
                this.enabledCheckbox = value;
                this.snackBarService.show("Chatbot status updated");
                this.viewConfig.setLoading(false);
                setTimeout(() => {
                  modalRef.close();
                  queueMicrotask(() =>
                    document
                      .getElementById("chatbot-detail-documents-table")
                      ?.focus(),
                  );
                }, 2000);
              },
              error: () => {
                this.snackBarService.show(
                  "Chatbot status update failed. Please try again after some time"
                );
                this.viewConfig.setLoading(false);
                this.enabledCheckbox = !value;
                modalRef.close();
              },
            });
        },
      },
    ];
    modalRef.dismissed.subscribe((data) => {
      this.enabledCheckbox = !value;
    });
  }
}
